import React, { useContext } from 'react';
import { useMutation } from '@apollo/react-hooks';
import get from 'lodash.get';

import { useServerData } from '@xingternal/hops';
import { ErrorContext } from '@xingternal/pages-page-error';
import { DataProxy } from '@apollo/client';


import {
  trackJobsBookmarkAction,
  trackJobsSubpageBookmarkAction,
} from '@xingternal/pages-tracking';

import { JobsContext } from '../JobsContext';
import { updateCache } from '../utils';
import JOB_BOOKMARK_MUTATION from './JobBookmarkMutation.graphql';
import JOB_UNBOOKMARK_MUTATION from './JobUnbookmarkMutation.graphql';
import JobsBookmark from './JobsBookmark';

type JobsBookmarkContainerProps = {
  jobId?: string;
  bookmarked: boolean;
  isSubpage?: boolean;
};

const JobsBookmarkContainer: React.FC<JobsBookmarkContainerProps> = ({
  jobId,
  bookmarked,
  isSubpage,
}) => {
  const { variables, query } = useContext(JobsContext);
  const { authenticated } = useServerData();

  const { showError } = useContext(ErrorContext);
  const [
    createBookmark,
    {
      error: createBookmarkError,
      loading: createBookmarkLoading,
      data: createBookmarkData,
    },
  ] = useMutation(JOB_BOOKMARK_MUTATION, {
    update: (cache, { data }) =>
      updateCache({
        cache: cache as DataProxy,
        query,
        jobId: jobId || '',
        variables,
        bookmarked: true,
        data,
      }),
  });

  const [
    deleteBookmark,
    {
      error: deleteBookmarkError,
      loading: deleteBookmarkLoading,
      data: deleteBookmarkData,
    },
  ] = useMutation(JOB_UNBOOKMARK_MUTATION, {
    update: (cache, { data }) =>
      updateCache({
        cache: cache as DataProxy,
        query,
        jobId: jobId || '',
        variables,
        bookmarked: false,
        data,
      }),
  });

  if (!authenticated) return null;
  return (
    <JobsBookmark
      createBookmark={() => createBookmark({ variables: { jobId } })}
      deleteBookmark={() => deleteBookmark({ variables: { jobId } })}
      loading={Boolean(createBookmarkLoading || deleteBookmarkLoading)}
      error={{
        showError: Boolean(
          createBookmarkError ||
            deleteBookmarkError ||
            get(deleteBookmarkData, 'jobDeleteBookmark.error') ||
            get(createBookmarkData, 'jobCreateBookmark.error')
        ),
        onError: () =>
          showError({
            message: 'EP_JOBS_FEEDBACK_ERROR',
            error:
              deleteBookmarkError ||
              createBookmarkError ||
              get(deleteBookmarkData, 'jobDeleteBookmark.error') ||
              get(createBookmarkData, 'jobCreateBookmark.error'),
          }),
      }}
      initialBookmark={bookmarked}
      trackingAction={
        isSubpage ? trackJobsSubpageBookmarkAction : trackJobsBookmarkAction
      }
    />
  );
};

export default JobsBookmarkContainer;
